import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchHeatmapData } from "../api/postgrest";
import { ThermoDetail, ThermoImage, ThermoPixel, VftCoefficients } from "../types";
import CenterLineChart from "./visualizations/CenterLineChart";
import StatsPanel from "./visualizations/StatsPanel";
import Plot from "react-plotly.js";
import { VISC_COLORSCALE } from "../colormaps";
import { generateTempColorscale } from "../utils/colormap";
import { useEscapeStack } from "../hooks/useEscapeStack";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useSwipeToDismiss } from "../hooks/useSwipeToDismiss";
import { viewportClassFromWidth } from "../utils/thicknessPlotBundle";
import { responsivePlotTier, useElementSize, type ResponsivePlotTier } from "../hooks/useElementSize";
import ModalCloseButton from "./ModalCloseButton";

const DRAW_COLORS = ["#6be7c5", "#ff9f9f", "#8ea6ff", "#f7d774", "#9ed2ff"];
type PlotShape = {
  x0?: number;
  y0?: number;
  x1?: number;
  y1?: number;
  type?: string;
  line?: { color?: string; width?: number };
};

type Props = {
  open: boolean;
  image: ThermoImage | null;
  detail: ThermoDetail | null;
  detailLoading?: boolean;
  detailError?: string | null;
  nasMountPath?: string;
  vftCoeffs?: VftCoefficients | null;
  onClose: () => void;
};

export default function DetailModal({
  open,
  image,
  detail,
  detailLoading = false,
  detailError = null,
  vftCoeffs,
  onClose
}: Props) {
  const [heatmapData, setHeatmapData] = useState<ThermoPixel[]>([]);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [shapes, setShapes] = useState<PlotShape[]>([]);
  const [viewportClass, setViewportClass] = useState(
    viewportClassFromWidth(typeof window === "undefined" ? 1400 : window.innerWidth)
  );
  const viscosityCardRef = useRef<HTMLDivElement | null>(null);
  const temperatureCardRef = useRef<HTMLDivElement | null>(null);
  const viscosityCardSize = useElementSize(viscosityCardRef);
  const temperatureCardSize = useElementSize(temperatureCardRef);
  useEffect(() => {
    const onResize = () => setViewportClass(viewportClassFromWidth(window.innerWidth));
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const viscosityTier = responsivePlotTier(
    viscosityCardSize.width || (viewportClass === "mobile" ? 420 : 760)
  );
  const temperatureTier = responsivePlotTier(
    temperatureCardSize.width || (viewportClass === "mobile" ? 420 : 760)
  );
  const plotHeightForTier = (tier: ResponsivePlotTier) => (tier === "wide" ? 420 : tier === "narrow" ? 440 : 460);
  useEscapeStack(open && !!image, onClose);
  useBodyScrollLock(open && !!image);
  const swipeToClose = useSwipeToDismiss<HTMLDivElement>({
    enabled: open && !!image,
    onDismiss: onClose
  });

  const { width, height, tempMatrix, viscMatrix, tempMin, tempMax, tempObservedMin } = useMemo(() => {
    if (!heatmapData.length) {
      return {
        width: 0,
        height: 0,
        tempMatrix: [],
        viscMatrix: [],
        tempMin: 600,
        tempMax: 600,
        tempObservedMin: 600
      };
    }
    let maxX = 0;
    let maxY = 0;
    let maxTemp = Number.NEGATIVE_INFINITY;
    let minTempVal = Number.POSITIVE_INFINITY;
    let minViscVal = Number.POSITIVE_INFINITY;
    heatmapData.forEach((p) => {
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
      if (p.temperature > maxTemp) maxTemp = p.temperature;
      if (p.temperature < minTempVal) minTempVal = p.temperature;
      if (p.viscosity < minViscVal) minViscVal = p.viscosity;
    });
    const w = maxX + 1;
    const h = maxY + 1;
    const temp = Array.from({ length: h }, () => Array(w).fill(NaN));
    const visc = Array.from({ length: h }, () => Array(w).fill(minViscVal));
    heatmapData.forEach((p) => {
      temp[p.y][p.x] = p.temperature;
      visc[p.y][p.x] = p.viscosity;
    });
    return {
      width: w,
      height: h,
      tempMatrix: temp,
      viscMatrix: visc,
      tempMin: minTempVal,
      tempMax: Math.max(600, maxTemp),
      tempObservedMin: minTempVal
    };
  }, [heatmapData]);

  const tempColorscale = useMemo(
    () => generateTempColorscale(tempMax || 1, 600),
    [tempMax]
  );

  useEffect(() => {
    if (!open || !image) return;
    setHeatmapLoading(true);
    setHeatmapError(null);
    fetchHeatmapData(image.id)
      .then((data) => setHeatmapData(data))
      .catch((err) => setHeatmapError(err instanceof Error ? err.message : "Heatmap load failed"))
      .finally(() => setHeatmapLoading(false));
  }, [open, image]);

  useEffect(() => {
    setDrawMode(false);
    setShapes([]);
  }, [image?.id, open]);

  const plotConfig = {
    responsive: true,
    displaylogo: false,
    editable: false,
    edits: {
      shapePosition: true,
      titleText: false,
      axisTitleText: false,
      colorbarTitleText: false,
      annotationText: false,
      annotationPosition: false,
      annotationTail: false,
      legendText: false
    },
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: ["drawline", "eraseshape"],
    scrollZoom: false
  };

  const centerLineCoords = useMemo(() => {
    const xVals = detail?.centerLine.map((p) =>
      typeof p.x === "number" ? p.x : Math.floor(width / 2)
    );
    const yVals = detail?.centerLine.map((p) => p.y);
    return { x: xVals || [], y: yVals || [] };
  }, [detail?.centerLine, width]);

  const clampPoint = useCallback(
    (pt: { x: number; y: number }) => ({
      x: Math.max(0, Math.min(width - 1, Math.round(pt.x))),
      y: Math.max(0, Math.min(height - 1, Math.round(pt.y)))
    }),
    [width, height]
  );

  const sampleLinePoints = useCallback(
    (start: { x: number; y: number }, end: { x: number; y: number }) => {
      if (!width || !height) return [];
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const steps = Math.max(Math.abs(dx), Math.abs(dy));
      if (steps === 0) {
        const tempVal = tempMatrix[start.y]?.[start.x];
        return Number.isFinite(tempVal) ? [{ x: start.x, y: start.y, temperature: tempVal }] : [];
      }
      const pts = [];
      for (let i = 0; i <= steps; i += 1) {
        const interpolated = { x: start.x + (dx * i) / steps, y: start.y + (dy * i) / steps };
        const { x, y } = clampPoint(interpolated);
        const tempVal = tempMatrix[y]?.[x];
        if (!Number.isFinite(tempVal)) continue;
        pts.push({ x, y, temperature: tempVal });
      }
      return pts;
    },
    [clampPoint, tempMatrix, width, height]
  );

  const mergeRelayoutShapes = useCallback((relayoutData: any, prev: PlotShape[]) => {
    if (!relayoutData) return prev;
    if (Array.isArray(relayoutData.shapes)) {
      return relayoutData.shapes as PlotShape[];
    }
    let next = [...prev];
    let changed = false;
    Object.entries(relayoutData).forEach(([key, value]) => {
      const match = key.match(/^shapes\[(\d+)\]\.(.+)$/);
      if (!match) return;
      const idx = Number(match[1]);
      if (value === null) {
        if (next[idx]) {
          next[idx] = undefined as any;
          changed = true;
        }
        return;
      }
      if (!next[idx]) next[idx] = {};
      (next[idx] as any)[match[2]] = value;
      changed = true;
    });
    return changed ? next.filter(Boolean) : prev;
  }, []);

  const handleRelayout = useCallback(
    (relayoutData: any) => {
      if (relayoutData?.dragmode) {
        setDrawMode(relayoutData.dragmode === "drawline");
      }
      setShapes((prev) => mergeRelayoutShapes(relayoutData, prev));
    },
    [mergeRelayoutShapes]
  );

  const colorizedShapes = useMemo(
    () =>
      shapes.map((shape, idx) => ({
        ...shape,
        type: shape.type || "line",
        line: {
          width: shape.line?.width ?? 3,
          color: shape.line?.color || DRAW_COLORS[idx % DRAW_COLORS.length]
        }
      })),
    [shapes]
  );

  const drawnProfiles = useMemo(
    () =>
      colorizedShapes
        .map((shape, idx) => {
          if (
            !Number.isFinite(shape.x0) ||
            !Number.isFinite(shape.y0) ||
            !Number.isFinite(shape.x1) ||
            !Number.isFinite(shape.y1)
          ) {
            return null;
          }
          const start = clampPoint({
            x: Number.isFinite(shape.x0) ? Number(shape.x0) : 0,
            y: Number.isFinite(shape.y0) ? Number(shape.y0) : 0
          });
          const end = clampPoint({
            x: Number.isFinite(shape.x1) ? Number(shape.x1) : start.x,
            y: Number.isFinite(shape.y1) ? Number(shape.y1) : start.y
          });
          return {
            id: `shape-${idx}`,
            color: shape.line?.color,
            points: sampleLinePoints(start, end).map((p) => ({
              y: p.y,
              temperature: p.temperature
            }))
          };
        })
        .filter((line): line is NonNullable<typeof line> => !!line && line.points.length > 0),
    [clampPoint, colorizedShapes, sampleLinePoints]
  );

  const handleDrawToggle = () => {
    setDrawMode((prev) => !prev);
  };

  const handleResetDrawings = () => {
    setShapes([]);
  };

  const thermoPlotBaseLayout = {
    font: { color: "#dbe7ff" },
    title: { text: "" },
    paper_bgcolor: "#0f1620",
    plot_bgcolor: "#0f1620",
    annotations: [],
    xaxis: {
      showgrid: false,
      showticklabels: false,
      showline: false,
      zeroline: false,
      ticks: "",
      title: { text: "" },
      color: "#dbe7ff",
      tickfont: { color: "#dbe7ff" },
      range: [0, Math.max(1, width - 1)],
      fixedrange: false
    },
    yaxis: {
      autorange: "reversed",
      showgrid: false,
      showticklabels: false,
      showline: false,
      zeroline: false,
      ticks: "",
      title: { text: "" },
      color: "#dbe7ff",
      tickfont: { color: "#dbe7ff" },
      range: [height, 0],
      scaleanchor: "x",
      scaleratio: 1,
      fixedrange: false
    }
  };

  const thermoColorbar = (title: string, tier: ResponsivePlotTier) => {
    const horizontal = tier === "phone";
    return {
      title: {
        text: title,
        side: horizontal ? "bottom" : "right",
        font: { color: "#dbe7ff" }
      },
      tickfont: { color: "#dbe7ff" },
      ...(horizontal
        ? {
            orientation: "h",
            x: 0.5,
            xanchor: "center",
            y: -0.18,
            yanchor: "top",
            len: 0.76,
            thickness: 12
          }
        : {})
    };
  };

  const thermoPlotLayout = (
    tier: ResponsivePlotTier,
    plotHeight: number,
    verticalColorbarMargin: number
  ) => ({
    ...thermoPlotBaseLayout,
    autosize: true,
    height: plotHeight,
    showlegend: false,
    margin:
      tier === "phone"
        ? { t: 8, r: 10, b: 82, l: 10 }
        : { t: 10, r: verticalColorbarMargin, b: 14, l: 10 },
    dragmode: drawMode ? "drawline" : "zoom",
    shapes: colorizedShapes
  });

  if (!open || !image) return null;

  return (
    <div className="modal-backdrop thermo-detail-backdrop">
      <div
        ref={swipeToClose.modalRef}
        className="modal thermo-detail-modal"
        data-tour-id="thermo-detail-modal"
      >
        <div className="modal-swipe-handle" aria-hidden="true" {...swipeToClose.handleProps} />
        <div className="thermo-detail-header modal-drag-region" {...swipeToClose.handleProps}>
          <div>
            <h3 style={{ margin: 0 }}>{image.filename}</h3>
            <p style={{ margin: 0, color: "var(--text-subtle)" }}>Sample #{image.id}</p>
          </div>
          <div className="thermo-detail-actions" data-tour-id="thermo-detail-actions">
            <button className="button secondary" onClick={handleDrawToggle}>
              {drawMode ? "Drawing: On" : "Drawing: Off"}
            </button>
            <button className="button secondary" onClick={handleResetDrawings}>
              Reset Lines
            </button>
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        <div style={{ marginTop: 8, color: "var(--text-subtle)", fontSize: 13 }}>
          Use plotly&apos;s Draw Line mode to add synced lines on either heatmap; lines mirror across
          both and feed the temperature-only profiles in the chart below. Reset clears all drawn
          lines.
        </div>

        <div
          className="grid two thermo-heatmap-grid"
          data-tour-id="thermo-detail-heatmaps"
          style={{
            marginTop: 12,
            marginBottom: 20,
            position: "relative",
            zIndex: 1,
            minHeight: 0
          }}
        >
          <div
            ref={temperatureCardRef}
            className="card thermo-plot-card"
            style={{
              background: "var(--surface-elevated)",
              position: "relative",
              overflow: "visible",
              minHeight: plotHeightForTier(temperatureTier) + 48
            }}
          >
            <div className="label">Temperature Heatmap</div>
            {height > 0 ? (
              <div className="thermo-plot-frame">
                <Plot
                  data={[
                    {
                      z: tempMatrix,
                      type: "heatmap",
                      colorscale: tempColorscale as any,
                      zmin: tempObservedMin,
                      zmax: tempMax,
                      colorbar: thermoColorbar("°C", temperatureTier)
                    },
                    {
                      x: centerLineCoords.x,
                      y: centerLineCoords.y,
                      mode: "lines",
                      line: { color: "#000000", width: 3 },
                      name: "Center line (y)"
                    },
                  ]}
                  layout={thermoPlotLayout(temperatureTier, plotHeightForTier(temperatureTier), 48)}
                  config={plotConfig}
                  style={{ width: "100%", minWidth: 320, height: plotHeightForTier(temperatureTier) }}
                  useResizeHandler
                  onRelayout={handleRelayout}
                />
              </div>
            ) : (
              <div style={{ color: "var(--text-faint)", padding: "12px 0" }}>
                {heatmapLoading ? "Loading heatmap…" : "No data"}
              </div>
            )}
          </div>
          <div
            ref={viscosityCardRef}
            className="card thermo-plot-card"
            style={{
              background: "var(--surface-elevated)",
              position: "relative",
              overflow: "visible",
              minHeight: plotHeightForTier(viscosityTier) + 48
            }}
          >
            <div className="label">Viscosity Heatmap</div>
            {height > 0 ? (
              <div className="thermo-plot-frame">
                <Plot
                  data={[
                    {
                      z: viscMatrix,
                      type: "heatmap",
                      colorscale: VISC_COLORSCALE as any,
                      colorbar: thermoColorbar("log10(η / dPa·s)", viscosityTier)
                    },
                    {
                      x: centerLineCoords.x,
                      y: centerLineCoords.y,
                      mode: "lines",
                      line: { color: "#000000", width: 3 },
                      name: "Center line (y)"
                    },
                  ]}
                  layout={thermoPlotLayout(viscosityTier, plotHeightForTier(viscosityTier), 82)}
                  config={plotConfig}
                  style={{ width: "100%", minWidth: 320, height: plotHeightForTier(viscosityTier) }}
                  useResizeHandler
                  onRelayout={handleRelayout}
                />
              </div>
            ) : (
              <div style={{ color: "var(--text-faint)", padding: "12px 0" }}>
                {heatmapLoading ? "Loading heatmap…" : "No data"}
              </div>
            )}
          </div>
        </div>

        {heatmapError && <div className="status-error">Heatmap error: {heatmapError}</div>}

        <div
          className="card thermo-centerline-card"
          data-tour-id="thermo-detail-centerline"
          style={{
            background: "var(--surface-elevated)",
            marginTop: 18,
            position: "relative",
            zIndex: 0
          }}
        >
          <CenterLineChart
            data={detail?.centerLine || []}
            title="Center Line Profile"
            loading={detailLoading}
            emptyMessage="No centerline data available for this sample."
            mode="single"
            vftCoeffs={vftCoeffs}
            drawnLines={drawnProfiles}
          />
          {detailError && (
            <div className="status-error" style={{ marginTop: 8 }}>
              Centerline error: {detailError}
            </div>
          )}
        </div>

        {detail && (
          <div style={{ marginTop: 12 }} data-tour-id="thermo-detail-stats">
            <StatsPanel
              title="Statistics"
              stats={detail.stats}
              tabs={[
                { label: "Center Line", stats: detail.stats },
                { label: "Hot Area | Gob | Bottle", stats: detail.bottleStats || detail.stats }
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
}
